import { IonApp, setupIonicReact, useIonLoading } from '@ionic/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useGate } from 'effector-react'
import { parse, stringify } from 'query-string'
import { HelmetProvider } from 'react-helmet-async'
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { Routing } from '~/pages'
import { CameraProvider } from '~/widgets/CameraProvider'
import { notificationsModel } from '~/entities/Notifications'
import { isDevEnv, isMobileApp } from '~/shared/config/env'
import { AppUrlListener } from '~/shared/lib/AppUrlListener'
import { featureFlagModel } from '~/shared/lib/featureFlag'
import { history } from '~/shared/lib/history'
import { loadingModel } from '~/shared/lib/loading'
import { NotificationModal } from '~/shared/lib/notificationModal'
import { NotificationsProvider } from '~/shared/lib/notifications'
import { sentryModel } from '~/shared/lib/sentry'
import { ErrorBoundary } from '~/shared/ui/ErrorBoundary/ErrorBoundary'
import { ReloadApp } from '~/shared/ui/ReloadApp'
import { ReloadSw } from '~/shared/ui/ReloadSw'
import { BODY_MAX_WIDTH } from '~/shared/ui/theme'

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css'

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css'
import '@ionic/react/css/structure.css'
import '@ionic/react/css/typography.css'

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css'
import '@ionic/react/css/float-elements.css'
import '@ionic/react/css/text-alignment.css'
import '@ionic/react/css/text-transformation.css'
import '@ionic/react/css/flex-utils.css'
import '@ionic/react/css/display.css'

import './App.css'

const queryClient = new QueryClient()

setupIonicReact({
  mode: 'ios',
})

export function App() {
  const loadingParams = useIonLoading()

  useGate(sentryModel.SentryGate)
  useGate(featureFlagModel.Gate)
  useGate(loadingModel.LoadingGate, loadingParams)
  useGate(notificationsModel.Gate)

  return (
    <ErrorBoundary>
      <CameraProvider>
        <IonApp
          style={{
            maxWidth: BODY_MAX_WIDTH,
            margin: '0 auto',
          }}
        >
          {!isMobileApp && <ReloadSw />}
          {isMobileApp && !isDevEnv && <ReloadApp />}

          <NotificationModal />
          <NotificationsProvider>
            <HistoryRouter history={history}>
              <AppUrlListener />
              <QueryParamProvider
                adapter={ReactRouter6Adapter}
                options={{
                  searchStringToObject: parse,
                  objectToSearchString: stringify,
                }}
              >
                <QueryClientProvider client={queryClient}>
                  <HelmetProvider>
                    <Routing />
                  </HelmetProvider>
                </QueryClientProvider>
              </QueryParamProvider>
            </HistoryRouter>
          </NotificationsProvider>
        </IonApp>
      </CameraProvider>
    </ErrorBoundary>
  )
}
